<div class="detail-page">
  <div class="header">
    <app-back-button
      parentRoute="/admin/orchestration/code-catalog"
      [useHistoryBack]="true"
      [title]="title()"
    ></app-back-button>
  </div>

  <app-loading *ngIf="loading()" [loading]="true"></app-loading>
  <div *ngIf="error() as message" class="error">{{ message }}</div>
  <div *ngIf="!loading() && notFound()" class="error">{{ 'Job not found in the code catalog.' | translate }}</div>

  <ng-container *ngIf="job() as job">
    <header class="title-block">
      <h1>
        {{ job.name }}
        <app-catalog-authors [authors]="job.authors"></app-catalog-authors>
      </h1>
      <p class="muted">
        {{ catalogGroupLabel(job.group) }} · {{ job.sourcePath }}
        · {{ 'Refs' | translate }}: {{ jobReferenceCount(job) }}
      </p>
      <p-tag
        *ngIf="isLegacyCronJob(job)"
        severity="warn"
        [value]="'not a process' | translate"
        [pTooltip]="'This job still owns business work and has not moved to a process assignment.' | translate"
        tooltipPosition="top"
      ></p-tag>
      <p-tag *ngIf="job.discoveredAtRuntime" severity="warn" value="runtime"></p-tag>
      <p-tag
        *ngIf="isCatalogItemObsolete(job)"
        severity="secondary"
        value="obsolete"
        pTooltip="Retired: marked [Obsolete]"
        tooltipPosition="top"
      ></p-tag>
      <p-tag
        *ngIf="!isOrchestrationCompliant(job)"
        severity="danger"
        value="logic"
        [pTooltip]="orchestrationIssuesTooltip(job)"
        tooltipPosition="top"
      ></p-tag>
    </header>

    <div class="info-tiles">
      <article class="info-tile">
        <header class="info-tile-header">
          <i class="pi pi-align-left" aria-hidden="true"></i>
          <h2>{{ 'Description' | translate }}</h2>
        </header>
        <p>{{ (job | serverTranslate:'descriptions') || job.description || ('No XML summary found on the job type.' | translate) }}</p>
      </article>

      <article class="info-tile" *ngIf="!isOrchestrationCompliant(job)">
        <header class="info-tile-header">
          <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
          <h2>{{ 'Orchestration issues' | translate }}</h2>
        </header>
        <ul class="info-tile-list">
          <li *ngFor="let issue of job.orchestrationIssues || []">{{ issue }}</li>
        </ul>
      </article>

      <article class="info-tile">
        <header class="info-tile-header">
          <i class="pi pi-cloud" aria-hidden="true"></i>
          <h2>{{ 'External services' | translate }}</h2>
        </header>
        <div class="external-services" *ngIf="job.externalServices?.length; else noServices">
          <p-tag
            *ngFor="let service of job.externalServices"
            severity="secondary"
            [value]="service.name || undefined"
            [pTooltip]="service.project + (service.entryPoint ? ' · ' + service.entryPoint : '')"
            tooltipPosition="top"
          ></p-tag>
        </div>
        <ng-template #noServices>
          <p class="muted">{{ 'No external services detected on the job' | translate }}</p>
        </ng-template>
      </article>

      <article class="info-tile">
        <header class="info-tile-header">
          <i class="pi pi-chart-line" aria-hidden="true"></i>
          <h2>{{ 'Status' | translate }}</h2>
        </header>
        <div class="info-tile-status">
          <p-tag
            [severity]="runtimeSeverity(job.runtimeStatus)"
            [value]="job.runtimeStatus || undefined"
          ></p-tag>
          <span class="muted">{{ job.registrationStatus }}</span>
          <span class="muted">{{ 'Scheduled' | translate }}: {{ job.isScheduled ? ('yes' | translate) : ('no' | translate) }}</span>
        </div>
      </article>

      <article class="info-tile">
        <header class="info-tile-header">
          <i class="pi pi-flag" aria-hidden="true"></i>
          <h2>{{ 'Feature flag' | translate }}</h2>
        </header>
        <p>
          {{ job.featureFlag || '—' }}
          <span *ngIf="job.featureFlagEnabled === true"> {{ '(on)' | translate }}</span>
          <span *ngIf="job.featureFlagEnabled === false"> {{ '(off)' | translate }}</span>
        </p>
      </article>

      <article class="info-tile info-tile-wide">
        <header class="info-tile-header">
          <i class="pi pi-cog" aria-hidden="true"></i>
          <h2>{{ 'Configuration' | translate }} ({{ job.configurationSections?.length || 0 }})</h2>
        </header>
        <div class="config-sections" *ngIf="job.configurationSections?.length; else noConfig">
          <details *ngFor="let section of job.configurationSections" class="config-section">
            <summary>
              <code>{{ section.section }}</code>
              <span class="muted" *ngIf="section.optionsType"> · {{ section.optionsType }}</span>
              <span class="muted" *ngIf="section.source"> · {{ section.source }}</span>
            </summary>
            <pre *ngIf="section.currentConfigJson; else noLiveConfig"><code>{{ formatJson(section.currentConfigJson) }}</code></pre>
            <ng-template #noLiveConfig>
              <p class="muted">{{ 'Live values unavailable for this section' | translate }}</p>
            </ng-template>
          </details>
        </div>
        <ng-template #noConfig>
          <p class="muted">{{ 'No IOptions / appsettings sections detected' | translate }}</p>
        </ng-template>
      </article>

      <article class="info-tile info-tile-wide">
        <header class="info-tile-header">
          <i class="pi pi-sitemap" aria-hidden="true"></i>
          <h2>{{ 'References commands' | translate }} ({{ jobReferenceCount(job) }})</h2>
        </header>
        <ul class="info-tile-list refs-list" *ngIf="job.referencedCommands?.length || job.usedCommands?.length; else noCommands">
          <li *ngFor="let command of job.referencedCommands || job.usedCommands || []">
            <app-copyable-identifier [value]="command" [link]="commandLink(command)"></app-copyable-identifier>
          </li>
        </ul>
        <ng-template #noCommands>
          <p class="muted">{{ 'No MediatR commands referenced' | translate }}</p>
        </ng-template>
      </article>
    </div>

    <section class="executions-section">
      <div class="section-heading">
        <h2>{{ 'Executions' | translate }}</h2>
        <a
          [routerLink]="monitoringLink(job.name || '')"
          [queryParams]="monitoringQueryParams(job.name || '')"
        >
          {{ formatExecutionSummary(job.executionCount, job.lastExecutedAt) }}
        </a>
      </div>
      <div class="orch-table-panel">
      <app-orchestration-table-heading heading="Executions" [(query)]="executionSearch">
      </app-orchestration-table-heading>
      <p class="muted" [pTooltip]="formatExecutionTooltip(job.executionCount, job.lastExecutedAt)" tooltipPosition="top">
        {{ 'from operation monitoring' | translate }} · Loki / Grafana
      </p>

      <app-loading *ngIf="executionsLoading()" [loading]="true"></app-loading>
      <div *ngIf="executionsError() as message" class="error">{{ message }}</div>

      <div class="table-wrapper" *ngIf="!executionsLoading()">
        <table class="executions-table">
          <thead>
            <tr>
              <th>{{ 'Started At' | translate }}</th>
              <th>{{ 'State' | translate }}</th>
              <th>{{ 'Duration' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let execution of visibleExecutions()"
              [class.selected]="execution.id === selectedExecutionId()"
              (click)="selectExecution(execution.id)"
            >
              <td>{{ (execution.startedAt || execution.createdAt) | date: 'dd.MM.yyyy HH:mm:ss' }}</td>
              <td>
                <p-tag
                  [value]="(execution.state || '—') | translate"
                  [severity]="getStateSeverity(execution.state)"
                ></p-tag>
              </td>
              <td>
                {{
                  execution.durationSeconds != null
                    ? (execution.durationSeconds | number: '1.0-1') + ' s'
                    : '—'
                }}
              </td>
              <td>
                <a
                  [routerLink]="monitoringLink(job.name || '')"
                  [fragment]="monitoringFragment(execution.id || '')"
                  (click)="$event.stopPropagation()"
                >
                  {{ 'Open in monitoring' | translate }}
                </a>
              </td>
            </tr>
            <tr *ngIf="!visibleExecutions().length">
              <td colspan="4" class="muted">{{ 'No monitored executions yet' | translate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </section>

    <section class="operation-logs" *ngIf="selectedExecutionId()">
      <div class="section-heading log-heading">
        <div>
          <h2>{{ 'Execution logs' | translate }}</h2>
          <span class="provider-label" *ngIf="operationLogsProvider()">{{ operationLogsProvider() }}</span>
        </div>
        <div class="log-actions">
          <a
            *ngIf="operationLogsExternalUrl() as url"
            pButton
            severity="secondary"
            icon="pi pi-external-link"
            [href]="url"
            target="_blank"
            rel="noopener noreferrer"
            [label]="'Open in external log viewer' | translate"
          ></a>
          <button
            pButton
            type="button"
            icon="pi pi-refresh"
            [label]="'Refresh logs' | translate"
            [disabled]="operationLogsLoading()"
            (click)="loadOperationLogs()"
          ></button>
        </div>
      </div>

      <div class="log-filters">
        <label>
          <span>{{ 'Log level' | translate }}</span>
          <select
            [ngModel]="operationLogLevel()"
            (ngModelChange)="operationLogLevel.set($event); loadOperationLogs()"
          >
            <option value="">{{ 'All levels' | translate }}</option>
            <option *ngFor="let level of logLevels" [value]="level">{{ level | translate }}</option>
          </select>
        </label>
        <label>
          <span>{{ 'Search execution logs' | translate }}</span>
          <input
            type="search"
            [ngModel]="operationLogSearch()"
            (ngModelChange)="operationLogSearch.set($event)"
            (keyup.enter)="loadOperationLogs()"
          />
        </label>
        <button pButton type="button" [label]="'Search' | translate" (click)="loadOperationLogs()"></button>
      </div>

      <app-loading *ngIf="operationLogsLoading()" [loading]="true"></app-loading>
      <div class="log-notice" *ngIf="!operationLogsLoading() && !operationLogsAvailable()">
        {{ (operationLogsMessage() || 'External log search is not configured.') | translate }}
      </div>
      <ng-container *ngIf="!operationLogsLoading() && operationLogsAvailable()">
        <div class="log-notice" *ngIf="operationLogsHasMore()">
          {{ 'More logs are available; refine the filters.' | translate }}
        </div>
        <div class="table-wrapper log-table-wrapper">
          <table class="log-table">
            <thead>
              <tr>
                <th>{{ 'Time' | translate }}</th>
                <th>{{ 'Level' | translate }}</th>
                <th>{{ 'Service' | translate }}</th>
                <th>{{ 'Message' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entry of operationLogs(); trackBy: trackLog">
                <td>{{ entry.timestamp | date: 'dd.MM.yyyy HH:mm:ss.SSS' }}</td>
                <td>
                  <p-tag
                    [value]="(entry.level || '—') | translate"
                    [severity]="getStateSeverity(entry.level)"
                  ></p-tag>
                </td>
                <td>
                  <strong>{{ entry.serviceName || '—' }}</strong>
                  <small *ngIf="entry.loggerName">{{ entry.loggerName }}</small>
                </td>
                <td class="log-message">
                  <span>{{ entry.message }}</span>
                  <small *ngIf="entry.traceId">
                    {{ 'Trace ID' | translate }}:
                    <app-copyable-identifier [value]="entry.traceId"></app-copyable-identifier>
                  </small>
                  <pre *ngIf="entry.exception"><code>{{ entry.exception }}</code></pre>
                </td>
              </tr>
              <tr *ngIf="!operationLogs().length">
                <td colspan="4" class="muted">{{ 'No logs found for this operation' | translate }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>
    </section>
  </ng-container>
</div>
